import { API_BASE } from './config.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown
  ) {
    super(message);
  }
}

/** The page's language decides the fallback text (rememberLang keeps <html lang> in step with the person's choice). */
const fallbackMessage = (): string =>
  typeof document !== 'undefined' && document.documentElement.lang === 'sv' ? 'Något gick fel. Försök igen om en stund.' : 'Something went wrong. Try again in a moment.';

export type Credentials = { kind: 'staff'; token: string; /** "Visa som": an admin's view token, sent next to their own sign-in */ viewAs?: string; /** the token to send, renewed when it runs out (auth.ts freshToken) */ fresh?: () => Promise<string> } | { kind: 'link'; token: string } | { kind: 'portal'; token: string };

/** JSON client for the Worker API. Staff send the Supabase token, freelancers their personal link, clients their portal link. */
export function createApi(creds: Credentials | null, fetchImpl: typeof fetch = (...a) => fetch(...a), base = API_BASE) {
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const raw = body instanceof Blob;
    if (body !== undefined) headers['Content-Type'] = raw ? 'application/octet-stream' : 'application/json';
    if (creds) headers.Authorization = creds.kind === 'staff' ? `Bearer ${creds.fresh ? await creds.fresh() : creds.token}` : creds.kind === 'portal' ? `Portal ${creds.token}` : `Link ${creds.token}`;
    if (creds?.kind === 'staff' && creds.viewAs) headers['X-View-As'] = creds.viewAs;
    // the freelancer link is Swedish unless its own switch says English (LinkPage sets the document's language); a
    // freelancer on a password is answered the same way, so staff send it too (the API uses an employee's saved choice)
    if (creds?.kind === 'link' || creds?.kind === 'staff') headers['Accept-Language'] = typeof document !== 'undefined' && document.documentElement.lang === 'en' ? 'en' : 'sv';
    const init = { method, headers, body: body === undefined ? undefined : raw ? body : JSON.stringify(body) };
    // a read that meets a busy server (5xx, a gateway page, a dropped connection) asks again twice before the page shows
    // "Try again": on launch day a full database pool made pages fail now and then (2026-11-02). Changes never repeat.
    let res: Response;
    for (let attempt = 0; ; attempt++) {
      const last = method !== 'GET' || attempt >= READ_RETRY_MS.length;
      try {
        res = await fetchImpl(`${base}${path}`, init);
      } catch (err) {
        if (last) throw err;
        await pause(READ_RETRY_MS[attempt]!);
        continue;
      }
      if (last || res.status < 500) break;
      await pause(READ_RETRY_MS[attempt]!);
    }
    const text = await res.text();
    let data: { error?: string; message?: string; details?: unknown } | null = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      // a proxy or gateway page (HTML) instead of the API's JSON: a server hiccup, never shown as a parser error
      throw new ApiError(res.status, 'bad_response', fallbackMessage());
    }
    if (!res.ok) throw new ApiError(res.status, data?.error ?? 'error', data?.message ?? fallbackMessage(), data?.details);
    return data as T;
  }
  // The last answer to each GET, so a page seen before shows at once (peek) while it asks again. Any change (POST, PUT,
  // DELETE) forgets them all: after a change a page waits for the fresh answer rather than show the old one.
  const seen = new Map<string, unknown>();
  const remember = (p: string, x: unknown) => {
    seen.delete(p);
    seen.set(p, x);
    if (seen.size > SEEN_MAX) seen.delete(seen.keys().next().value!);
  };
  const change = <T>(method: string, p: string, b?: unknown) =>
    call<T>(method, p, b).finally(() => seen.clear());
  return {
    get: <T>(p: string) =>
      call<T>('GET', p).then((x) => {
        remember(p, x);
        return x;
      }),
    /** The last answer to this GET in this session, if any: shown while the page asks again. */
    peek: <T>(p: string): T | undefined => seen.get(p) as T | undefined,
    post: <T>(p: string, b?: unknown) => change<T>('POST', p, b ?? {}),
    put: <T>(p: string, b?: unknown) => change<T>('PUT', p, b ?? {}),
    /** a file's bytes as they are (upload chunks) */
    putBytes: <T>(p: string, b: Blob) => change<T>('PUT', p, b),
    del: <T>(p: string) => change<T>('DELETE', p)
  };
}
/** `peek` is optional so test doubles need not keep answers. */
export type Api = Omit<ReturnType<typeof createApi>, 'peek'> & Partial<Pick<ReturnType<typeof createApi>, 'peek'>>;

/** Waits before a read asks again (GET only); `pause` is swapped out in tests. */
const READ_RETRY_MS = [400, 1200];
let pause = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
export const setRetryPause = (f: (ms: number) => Promise<void>) => {
  pause = f;
};

/** How many GET answers a session keeps for showing at once (the main pages and a few projects or clients). */
const SEEN_MAX = 40;

/** The last answer to a GET in this session, if any (also for test doubles without a memory). */
export const peek = <T>(api: Api, path: string): T | undefined => api.peek?.<T>(path);
